import { App, Button } from 'antd';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router';
import type { ArrangementType } from '@pams/shared';
import { useAuth } from '@/hooks/useAuth';
import { mayRevertRunning } from '@/lib/accessRole';
import { api, type ApiError } from '@/lib/api';
import { listAfterLeaving, listOriginOf, originForOrder, type ListOrigin } from '@/lib/listOrigin';
import { queryKeys } from '@/lib/queryKeys';
import { DELETE_REVERTED_COPY } from './copy';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7B §6c] "DELETE PERMANENTLY" ON A DRAFT A RUNNING ORDER WAS TAKEN BACK INTO.
 *
 *  ⚠️ TWO EXISTING ACTS IN SEQUENCE, AND NO NEW ENDPOINT: the ordinary Discard (`POST …/archive`) and
 *  then the ordinary permanent delete (`DELETE …`), which the server allows only for an ARCHIVED DRAFT —
 *  so this cannot reach anything the two of them do not already reach. Offered to the same
 *  `master_superadmin` holder who could take the order back, and only on a draft carrying
 *  `revertedToDraft`. An order discarded already skips the first step.
 *
 *  ⚠️ `modal.confirm`, NOT A CONTROLLED `<Modal>` — there is no input to validate, and it keeps the
 *  overlay census where it is.
 *
 *  ⚠️ [TYPED-BACK] THE ORDER IS GONE AFTERWARDS, SO IT LANDS ON A LIST — the one it was opened from
 *  (`?from=`, read off the record page), else Manage Arrangements (`listAfterLeaving`). The origin is
 *  kept only for an order of the list's own type (`originForOrder`), exactly as the record page keeps
 *  it, so a hand-made `?from=` cannot land an untyped order on a typed list. ⚠️ [amended TYPE-FILTER] With
 *  no origin a typed order went to its own type's list; Manage Arrangements lists every order now, so it
 *  lands there like any other.
 *
 *  ⚠️ IT READS THE ORIGIN OFF THE LOCATION FOR SYMMETRY WITH `UnpublishAction`, its neighbour in the
 *  record page's header — not for a hidden caller: `pages/ArrangementView` is its one caller. The TYPE is
 *  the record's and comes as a prop, REQUIRED though it may be `undefined`: it decides which origin is
 *  kept, and a prop that decides where the operator lands is never defaulted — a caller that forgot it
 *  would drop every typed order's way back to the list it came from.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const DeleteRevertedDraftAction = ({
  arrangementId,
  type,
  home,
  isDraft,
  archived,
  reverted,
}: {
  arrangementId: string;
  /** The order's type — `undefined` for an untyped order. Required: see the header. */
  type: ArrangementType | undefined;
  /**
   * [DP2026] Where the order is listed with no origin (`homeListOf`) — its type's list for a draft made from its
   * own type's booklet, `null` otherwise. Required, for `type`'s reason: it decides where the operator lands.
   */
  home: ListOrigin | null;
  isDraft: boolean;
  archived: boolean;
  reverted: boolean;
}): React.ReactElement | null => {
  const { officer } = useAuth();
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const origin = originForOrder(listOriginOf(useLocation().search), type);
  const queryClient = useQueryClient();

  const destroy = useMutation({
    mutationFn: async (): Promise<void> => {
      if (!archived) await api.post(`/arrangements/${arrangementId}/archive`);
      await api.delete(`/arrangements/${arrangementId}`);
    },
    onSuccess: () => {
      void message.success(DELETE_REVERTED_COPY.done);
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.all });
      void navigate(listAfterLeaving(origin ?? home));
    },
    onError: (error: ApiError) => {
      void message.error(error.message);
      void queryClient.invalidateQueries({ queryKey: queryKeys.arrangements.all });
    },
  });

  if (!mayRevertRunning(officer) || !isDraft || !reverted) return null;

  return (
    <Button
      danger
      data-testid="delete-reverted"
      loading={destroy.isPending}
      onClick={() => {
        modal.confirm({
          title: DELETE_REVERTED_COPY.title,
          content: <span className="text-sm">{DELETE_REVERTED_COPY.body}</span>,
          okText: DELETE_REVERTED_COPY.ok,
          okButtonProps: { danger: true },
          onOk: () => {
            destroy.mutate();
          },
        });
      }}
    >
      {DELETE_REVERTED_COPY.action}
    </Button>
  );
};
